// ============================================================================
// RaidArenas — three concrete arena renderers (Timeline / Constellation /
// War Room) plus the default Battlefield. Each one reads the same
// {activeRaid, target, stats} triple so switching is instantaneous and there
// is no per-view fetch. Kept in one file to keep the surface small; if any
// arena grows beyond ~150 LOC, promote it into its own module.
// ============================================================================

import { useMemo } from 'react'
import type { ActiveRaidModel, ChecklistItem, RaidView } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import type { RaidTargetStats } from '../../utils/raidTargeting'
import { EnrageTimer } from './EnrageTimer'
import { StatusChip, type StatusPillTone } from '../Common'
import styles from './Raid.module.css'

export interface ArenaProps {
  activeRaid: ActiveRaidModel
  target: TimelineElement | null
  stats: RaidTargetStats
  hpPercent: number
  sortedLeaderboard: Array<{ userEmail: string; userDisplayName: string; damageDealt: number }>
}

const STATUS_TONE: Record<string, StatusPillTone> = {
  COMPLETE: 'success',
  IN_PROGRESS: 'info',
  TODO: 'muted',
}

// ---------------------------------------------------------------------------
// Battlefield — the classic HP + leaderboard view (formerly inlined in
// RaidLayout). Extracted here so the Rail switcher can render it uniformly.
// ---------------------------------------------------------------------------

export const BattlefieldArena = ({ activeRaid, hpPercent, sortedLeaderboard, stats }: ArenaProps) => (
  <div className={styles.arenaBattlefield}>
    <div className={styles.arenaHpStrip}>
      <div className={styles.arenaHpHead}>
        <strong className={styles.arenaHpValue}>
          {activeRaid.currentBossHp} / {activeRaid.initialBossHp} HP
        </strong>
        <span className={styles.arenaHpMeta}>
          {stats.subtreeCompletedCount}/{stats.subtreeSize} nodes complete · {stats.completedChecklistCount}/{stats.nestedChecklistCount} checklists
        </span>
      </div>
      <div className={styles.arenaHpBar}>
        <div className={styles.arenaHpBarFill} style={{ width: `${hpPercent}%` }} />
      </div>
    </div>

    <div className={styles.arenaLeaderboardCard}>
      <div className={styles.arenaEyebrow}>Damage Leaderboard</div>
      {sortedLeaderboard.length === 0 ? (
        <p className={styles.arenaMuted}>
          Complete checklists, tasks, or focus sprints to chip away at the boss.
        </p>
      ) : (
        <ol className={styles.arenaLeaderboardList}>
          {sortedLeaderboard.map((entry, index) => (
            <li key={entry.userEmail} className={styles.arenaLeaderboardRow}>
              <span className={styles.arenaLeaderboardRank}>{index + 1}</span>
              <span className={styles.arenaLeaderboardPlayer}>{entry.userDisplayName}</span>
              <span className={styles.arenaLeaderboardDmg}>{entry.damageDealt} DMG</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// War Room — a fullscreen-feeling variant with an oversized enrage clock,
// the boss headline, and a top-3 kill feed. Same underlying data.
// ---------------------------------------------------------------------------

export const WarRoomArena = ({ activeRaid, hpPercent, sortedLeaderboard }: ArenaProps) => (
  <div className={styles.arenaWarRoom}>
    <div className={styles.arenaWarClock}>
      <EnrageTimer deadlineIso={activeRaid.enrageDeadlineTimestamp} />
    </div>
    <h3 className={styles.arenaWarBossName}>{activeRaid.bossTitle}</h3>
    <div className={styles.arenaWarHp}>
      <div className={styles.arenaWarHpTrack}>
        <div className={styles.arenaWarHpFill} style={{ width: `${hpPercent}%` }} />
      </div>
      <span className={styles.arenaWarHpValue}>
        {activeRaid.currentBossHp} / {activeRaid.initialBossHp}
      </span>
    </div>
    <div className={styles.arenaWarFeed}>
      <div className={styles.arenaEyebrow}>Kill feed</div>
      {sortedLeaderboard.length === 0 ? (
        <p className={styles.arenaMuted}>No damage recorded yet.</p>
      ) : (
        sortedLeaderboard.slice(0, 5).map((entry, index) => (
          <div key={entry.userEmail} className={styles.arenaWarFeedRow}>
            <span className={styles.arenaWarFeedRank}>#{index + 1}</span>
            <strong>{entry.userDisplayName}</strong>
            <em>{entry.damageDealt} DMG</em>
          </div>
        ))
      )}
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Constellation — inline SVG boss core surrounded by task satellites.
// Completed tasks glow green, in-progress ones amber, pending ones outlined.
// ---------------------------------------------------------------------------

export const ConstellationArena = ({ activeRaid, stats, hpPercent }: ArenaProps) => {
  const satellites = useMemo(() => {
    const nodes = stats.subtreeElements.length > 0
      ? stats.subtreeElements
      : stats.directChildren
    return nodes.slice(0, 24)
  }, [stats])

  const centerX = 200
  const centerY = 200
  const radius = 130

  return (
    <div className={styles.arenaConstellationShell}>
      <svg
        className={styles.arenaConstellationSvg}
        viewBox="0 0 400 400"
        role="img"
        aria-label={`Constellation view of ${activeRaid.bossTitle}`}
      >
        {satellites.map((node, index) => {
          const angle = (index / Math.max(1, satellites.length)) * Math.PI * 2
          const cx = centerX + Math.cos(angle) * radius
          const cy = centerY + Math.sin(angle) * radius
          const done = node.boardStatus === 'COMPLETE'
          const progress = node.boardStatus === 'IN_PROGRESS'
          return (
            <g key={node.id}>
              <line
                x1={centerX} y1={centerY} x2={cx} y2={cy}
                stroke="currentColor" strokeOpacity={done ? 0.6 : 0.18} strokeWidth={1}
              />
              <circle
                cx={cx} cy={cy} r={done ? 10 : progress ? 8 : 6}
                className={done ? styles.arenaConstDone : progress ? styles.arenaConstLive : styles.arenaConstPending}
              />
              <text
                x={cx} y={cy - 14}
                textAnchor="middle"
                className={styles.arenaConstLabel}
              >
                {node.title.length > 16 ? `${node.title.slice(0, 15)}…` : node.title}
              </text>
            </g>
          )
        })}
        <circle
          cx={centerX} cy={centerY} r={54}
          className={styles.arenaConstCore}
          style={{ '--core-glow': `${Math.round(hpPercent)}%` } as React.CSSProperties}
        />
        <text x={centerX} y={centerY - 4} textAnchor="middle" className={styles.arenaConstCoreTitle}>
          {activeRaid.currentBossHp}
        </text>
        <text x={centerX} y={centerY + 16} textAnchor="middle" className={styles.arenaConstCoreSubtitle}>
          / {activeRaid.initialBossHp} HP
        </text>
      </svg>
      <p className={styles.arenaMuted}>
        Boss core in the center. Each satellite is a task in the target subtree — completed nodes brighten and burn the connecting thread.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Timeline — a compact strip of the target subtree grouped by boardStatus.
// Reads the same subtreeElements the constellation uses.
// ---------------------------------------------------------------------------

export const TimelineArena = ({ stats, target }: ArenaProps) => {
  const buckets = useMemo(() => {
    const done: TimelineElement[] = []
    const live: TimelineElement[] = []
    const pending: TimelineElement[] = []
    stats.subtreeElements.forEach((node) => {
      if (node.boardStatus === 'COMPLETE') done.push(node)
      else if (node.boardStatus === 'IN_PROGRESS') live.push(node)
      else pending.push(node)
    })
    return { done, live, pending }
  }, [stats])

  const totalChecklists = (elementId: string): ChecklistItem[] =>
    stats.checklistBucket[elementId] ?? []

  const renderColumn = (label: string, kind: keyof typeof buckets, nodes: TimelineElement[]) => (
    <section className={styles.arenaTimelineColumn} data-kind={kind}>
      <header className={styles.arenaTimelineColumnHeader}>
        <span className={styles.arenaEyebrow}>{label}</span>
        <strong>{nodes.length}</strong>
      </header>
      {nodes.length === 0 ? (
        <p className={styles.arenaMuted}>Empty</p>
      ) : (
        nodes.slice(0, 24).map((node) => {
          const checklists = totalChecklists(node.id)
          return (
            <article key={node.id} className={styles.arenaTimelineCard}>
              <div className={styles.arenaTimelineCardHead}>
                <StatusChip
                  size="sm"
                  tone={STATUS_TONE[node.boardStatus ?? 'TODO'] ?? 'muted'}
                >
                  {node.type}
                </StatusChip>
                <strong>{node.title}</strong>
              </div>
              {checklists.length > 0 && (
                <div className={styles.arenaTimelineChecklists}>
                  {checklists.slice(0, 4).map((item) => (
                    <span
                      key={item.id}
                      className={item.isCompleted ? styles.arenaTimelineCheckDone : styles.arenaTimelineCheckPending}
                      title={item.title}
                    >
                      {item.isCompleted ? '✓' : '○'} {item.title.slice(0, 22)}
                    </span>
                  ))}
                  {checklists.length > 4 && (
                    <span className={styles.arenaMuted}>+{checklists.length - 4} more</span>
                  )}
                </div>
              )}
            </article>
          )
        })
      )}
    </section>
  )

  if (!target) {
    return <p className={styles.arenaMuted}>Target not resolved.</p>
  }

  return (
    <div className={styles.arenaTimelineGrid}>
      {renderColumn('Pending', 'pending', buckets.pending)}
      {renderColumn('In progress', 'live', buckets.live)}
      {renderColumn('Complete', 'done', buckets.done)}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Public switch used by the RaidLayout — keeps the switch statement in one
// place so adding a new arena only means adding a component here.
// ---------------------------------------------------------------------------

export const renderRaidArena = (
  view: RaidView | 'BATTLEFIELD',
  props: ArenaProps,
) => {
  if (view === 'BATTLEFIELD') return <BattlefieldArena {...props} />
  if (view === 'WAR_ROOM') return <WarRoomArena {...props} />
  if (view === 'CONSTELLATION') return <ConstellationArena {...props} />
  if (view === 'TIMELINE') return <TimelineArena {...props} />
  return <BattlefieldArena {...props} />
}
